import { useEffect, useId, useRef } from "react";
import type { PropsWithChildren } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "./ModalSheet.css";

type ModalSheetProps = PropsWithChildren<{
  open: boolean;
  title: string;
  closeLabel: string;
  onClose: () => void;
  className?: string;
  lang?: string;
}>;

export function ModalSheet({
  open,
  title,
  closeLabel,
  onClose,
  className = "",
  lang,
  children,
}: ModalSheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();

  useEffect(() => {
    if (!open) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    dialog.showModal();
    document.body.style.overflow = "hidden";
    closeRef.current?.focus({ preventScroll: true });

    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;

      if (trigger instanceof HTMLElement && trigger.isConnected) {
        trigger.focus({ preventScroll: true });
      }
    };
  }, [open]);

  return createPortal(
    <dialog
      ref={dialogRef}
      className={`modal-sheet ${className}`}
      lang={lang}
      aria-labelledby={headingId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;

        const bounds = event.currentTarget.getBoundingClientRect();
        const outside =
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom;

        if (outside) onClose();
      }}
    >
      <div className="modal-sheet-header">
        <h2 id={headingId}>{title}</h2>
        <button
          ref={closeRef}
          className="modal-sheet-close"
          type="button"
          aria-label={closeLabel}
          onClick={onClose}
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>
      {open && children}
    </dialog>,
    document.body,
  );
}
